import { useEffect, useState } from "react";
import { useMe } from "@/data/user";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints";
import { useModalAction } from "@/contexts/modal/Context.tsx";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { maixpanelTrackEventProps } from "@/mixpanel/MixpanelUtils";
import { AvailableDaysItem, LoginUserItem } from "@/types";
import dayjs, { Dayjs } from "dayjs";
import Button from "@edusogno/primitives/button";
import Warning from "@/components/ui/Warning";
import HourPicker from "@edusogno/composites/hours";
import PageHeading from "@/components/ui/PageHeading";
import CustomDatePicker from "@edusogno/composites/calendar";

interface DesktopViewProps {
  date: Dayjs;
  setDate: (date: Dayjs) => void;
  availableDays: Dayjs[];
  availableDaysArray: AvailableDaysItem[];
}

interface StateProps {
  exerciseCorrection: string;
}

const BookMockTestDesktopView = ({
  date,
  setDate,
  availableDays,
  availableDaysArray,
}: DesktopViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateData = location?.state as StateProps;
  const breakpoint = useBreakpoint();
  const { t, i18n } = useTranslation();
  const { openModal } = useModalAction();
  const { exerciseCorrection } = stateData;

  const [hour, setHour] = useState<string | null>(null);
  const [selectedTutor, setSelectedTutor] = useState<string>();

  const isLarge = ["lg"].indexOf(breakpoint) !== -1;

  // mixpanel integration
  const { me } = useMe();
  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Exercise", me?.item?.user as LoginUserItem),
  );

  useEffect(() => {
    try {
      const defaultDate = availableDays[0].format("MM/DD/YY");
      setDate(dayjs(defaultDate));
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDays.length]);

  return (
    <>
      <PageHeading
        title={t("mock_test.header")}
        subtitle={t("mock_test.sub_header")}
        className="until-lg:px-4 until-lg:pb-0"
      />

      <div className="w-full h-[--content-height] flex flex-col gap-4">
        <div className="h-full w-full bg-white rounded-md">
          <div className="w-full h-full flex-col justify-between items-center flex bg-white rounded-md py-3 2xl:py-8 px-6">
            <div className="flex flex-col gap-1 w-full mb-4">
              <h2 className="text-xl font-semibold text-secondary">
                {t("mock_test.title")}
              </h2>

              <div className="flex flex-row gap-1  text-base">
                <Trans
                  i18nKey={t("mock_test.subtitle")}
                  components={{
                    b: <b className="font-bold text-brand-deep" />,
                  }}
                />
              </div>
            </div>

            <>
              <div className="grid grid-cols-2 h-full w-full gap-8 2xl:max-w-[1250px] xl:mx-auto 3xl:min-h-[50cqh] min-h-[60%] justify-center ">
                <div className="flex-s h-1/2 2xl:h-full  justify-center w-full gap-x-4 mx-auto ">
                  <CustomDatePicker
                    lang={i18n.language as "en" | "it" | "es" | "fr" | "de"}
                    openDates={availableDays}
                    onValueChange={(value) => {
                      if (value) {
                        setDate(dayjs(value as string));
                      }
                    }}
                    value={date.format("MM/DD/YY")}
                    type={"single"}
                    className={`w-full !max-w-full h-full ${
                      isLarge ? "custom-calendar" : ""
                    }`}
                  />
                </div>

                <div className="w-full flex-[1_1_50%] h-full overflow-x-hidden overflow-y-auto">
                  <HourPicker
                    type="single"
                    value={hour!}
                    openHours={availableDaysArray?.map((cal) =>
                      dayjs(cal.date).format("HH:mm"),
                    )}
                    hours={availableDaysArray?.map((cal) =>
                      dayjs(cal.date).format("HH:mm"),
                    )}
                    onValueChange={(value) => {
                      if (value) {
                        setHour(value as string);
                        const tutor = availableDaysArray.find(
                          (el) =>
                            el.date ===
                            `${date.format("YYYY-MM-DD")} ${value as string}`,
                        );
                        if (tutor) {
                          setSelectedTutor(tutor.tutor);
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div className="mt-4">
                <Warning
                  text={
                    <Trans
                      i18nKey="one_to_one.warning_timezome"
                      components={{
                        b: <b className="font-semibold" />,
                      }}
                    ></Trans>
                  }
                />
              </div>

              <div className="mt-6 mx-auto flex flex-row items-center justify-center max-w-2xl gap-x-5 w-full">
                <Button
                  data-track-props={mixpanelPropsData}
                  data-track-eventtype={"Exercise_MockTest_Exit"}
                  size="md"
                  onClick={() => navigate(-1)}
                  className="!text-light"
                >
                  {t("btns.exit")}
                </Button>

                <Button
                  data-track-props={mixpanelPropsData}
                  data-track-eventtype={"Exercise_MockTest_Confirm"}
                  size="md"
                  type="submit"
                  color="green"
                  disabled={
                    typeof hour === "undefined" || hour == null || date === null
                  }
                  onClick={() => {
                    openModal(
                      "CONFIRM_MOCK_TEST_MODAL",
                      {
                        date,
                        hour,
                        exercise: exerciseCorrection,
                        tutor: selectedTutor,
                      },
                      {
                        heading: t(
                          "one_to_one.confirm_first_session_modal.title",
                        ),
                      },
                    );
                  }}
                >
                  {t("btns.confirm")}
                </Button>
              </div>
            </>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookMockTestDesktopView;
